import { Dispatch, Fragment, ReactNode, SetStateAction, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Checkbox } from '@/modules/shared/components/checkbox/Checkbox'
import { ColumnDefinition, LineProps, MultiRowSelection } from '@/modules/shared/components/table/types'
import { ChevronDownIcon } from '@/modules/shared/icons/ChevronDownIcon'
import { classNames } from '@/modules/shared/utils/classNames'

/** Indentation (rem) applied per tree depth level. */
const DEFAULT_TREE_INDENT_REM = 1.5

interface TableRowProps<T> extends LineProps<T> {
  record: T
  index: number
  columns: ColumnDefinition<T>[]
  className?: string
  onRowClick?: (record: T, index: number) => void
  setSelectedRows: Dispatch<SetStateAction<T[]>>
  selectedRows: T[]
  multiRowSelection?: MultiRowSelection<T>
  keyExtractor?: (record: T, index: number) => string
  selectedRowsFromProps?: T[]
  renderFullWidthRow?: (record: T, index: number) => ReactNode | null
  /** Tree depth of this row (0 = top level). When set, the row renders in tree mode with indentation. */
  treeLevel?: number
  /** Whether this row can be expanded to reveal children. */
  treeExpandable?: boolean
  /** Whether this row is currently expanded. */
  treeExpanded?: boolean
  /** Toggle the expanded state of this row. */
  onToggleExpand?: () => void
  /** Whether this row's children are currently being fetched. */
  treeChildrenLoading?: boolean
}

export default function TableRow<T>({
  record,
  index,
  columns,
  className,
  onRowClick,
  hasExtraLine,
  getSubLines,
  setSelectedRows,
  selectedRows = [],
  multiRowSelection,
  keyExtractor,
  selectedRowsFromProps,
  renderFullWidthRow,
  treeLevel,
  treeExpandable,
  treeExpanded,
  onToggleExpand,
  treeChildrenLoading,
}: TableRowProps<T>) {
  const { t } = useTranslation()
  // Key to identify a row/record in the table
  const keyIdentifier = multiRowSelection?.key as keyof T

  const isTreeRow = treeLevel !== undefined

  // The tree toggle (chevron/indent) is injected into the first visible column, keeping every other cell aligned.
  const visibleColumns = columns.filter((column) => !column?.hidden || column?.alwaysVisible)
  const treeToggleColumnKey = isTreeRow ? visibleColumns[0]?.key : undefined

  // Check if the current row/record is selected
  const isRowSelected = useMemo(
    () =>
      selectedRowsFromProps
        ? Boolean(selectedRowsFromProps.find((row) => keyExtractor?.(row, index) === keyExtractor?.(record, index)))
        : Boolean(selectedRows.find((row) => row[keyIdentifier] === record[keyIdentifier])),
    [selectedRowsFromProps, selectedRows, keyIdentifier, keyExtractor, record, index]
  )

  const onRowSelect = () => {
    setSelectedRows((prevState) => {
      // `prevSelectedRows` and `selectedRows` are the same. But we should use `prevState` in the callback to get the latest updated state
      const prevSelectedRows = prevState
      if (!isRowSelected) {
        // If not selected, push the current row/record to the the list of selected rows
        return [...prevSelectedRows, record]
      } else {
        // If selected, remove the current row/record from the list of selected rows
        return prevSelectedRows.filter((row) => row[keyIdentifier] !== record[keyIdentifier])
      }
    })
  }

  const fullWidthContent = renderFullWidthRow?.(record, index)
  const hasFullWidthContent = fullWidthContent != null
  const visibleColumnCount = columns.filter((column) => !column.hidden || column.alwaysVisible).length

  return (
    <tr
      className={classNames(
        {
          'border-b': hasExtraLine ? !hasExtraLine(record) || (getSubLines && getSubLines(record).length) : true,
          'cursor-pointer hover:bg-gray-50': !!onRowClick && !hasFullWidthContent,
          'bg-primary/10': isRowSelected && !hasFullWidthContent,
          'hover:bg-gray-50': !isRowSelected && !hasFullWidthContent,
        },
        className
      )}
      onClick={() => onRowClick && !hasFullWidthContent && onRowClick(record, index)}
    >
      {hasFullWidthContent ? (
        <td colSpan={visibleColumnCount + (multiRowSelection ? 1 : 0)} className="p-0">
          {fullWidthContent}
        </td>
      ) : (
        <>
          {multiRowSelection && (
            <td className="px-3">
              <Checkbox
                square
                aria-label={`Select row ${index + 1}`}
                dataTestId={`select-row-${index + 1}`}
                onChange={() => onRowSelect()}
                isSelected={isRowSelected}
              />
            </td>
          )}
          {columns.map((column) => (
            <Fragment key={column?.key}>
              {(!column?.hidden || column?.alwaysVisible) && (
                <td
                  // tailwind css does not support dynamic values (Ref: https://v2.tailwindcss.com/docs/just-in-time-mode#arbitrary-value-support)
                  style={{ maxWidth: `${column?.maxWidth}rem`, minWidth: `${column?.minWidth}rem` }}
                  className={classNames(column?.cellStyles, 'break-words', {
                    'w-full': column?.grow,
                  })}
                >
                  {isTreeRow && column?.key === treeToggleColumnKey ? (
                    <div className="flex items-stretch">
                      {Array.from({ length: treeLevel ?? 0 }).map((_, depth) => (
                        <span
                          key={depth}
                          aria-hidden
                          className="relative shrink-0"
                          style={{ width: `${DEFAULT_TREE_INDENT_REM}rem` }}
                        >
                          <span className="pointer-events-none absolute -inset-y-3 left-1/2 w-px -translate-x-1/2 bg-gray-200" />
                        </span>
                      ))}
                      <div className="flex min-w-0 flex-1 items-center">
                        {treeExpandable ? (
                          treeChildrenLoading ? (
                            <span className="mr-3 flex size-6 shrink-0 items-center justify-center" role="status">
                              <span className="size-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
                              <span className="sr-only">{t('common.loading', 'Loading')}...</span>
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation()
                                onToggleExpand?.()
                              }}
                              className="mr-3 flex size-6 shrink-0 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-100"
                              data-testid={`tree-row-toggle-${treeLevel}`}
                              aria-label={
                                treeExpanded
                                  ? t('table.collapseRow', 'Collapse Row')
                                  : t('table.expandRow', 'Expand Row')
                              }
                            >
                              <ChevronDownIcon
                                className={classNames('size-5 transition-transform', { '-rotate-90': !treeExpanded })}
                              />
                            </button>
                          )
                        ) : (
                          <span className="mr-3 size-6 shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">{column?.render(record, index)}</div>
                      </div>
                    </div>
                  ) : (
                    column?.render(record, index)
                  )}
                </td>
              )}
            </Fragment>
          ))}
        </>
      )}
    </tr>
  )
}
